<!-- AnnualPermitModal.vue -->
<template>
  <div class="modal fade" id="annualPermitModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-card-account-details text-warning me-2"></i>
            Issue Annual Park Permit
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="issuePermit">
          <div class="modal-body">
            <!-- Permit Type Selection -->
            <div class="row mb-4">
              <div class="col-12">
                <label class="form-label fw-semibold">Select Permit Type</label>
                <div class="row g-3">
                  <div class="col-md-4" v-for="type in permitTypes" :key="type.value">
                    <div class="card border cursor-pointer permit-type-card"
                         :class="{'border-warning bg-light': permitForm.permitType === type.value}"
                         @click="selectPermitType(type.value)">
                      <div class="card-body text-center p-3">
                        <i :class="type.icon + ' font-size-24 mb-2'"
                           :style="{color: permitForm.permitType === type.value ? '#ffc107' : '#6c757d'}"></i>
                        <h6 class="mb-1">{{ type.label }}</h6>
                        <small class="text-muted">{{ type.description }}</small>
                        <div class="mt-2">
                          <strong class="text-primary">{{ formatPrice(type.price) }}/year</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Permit Holder Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Permit Holder Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Full Name / Organization *</label>
                <input type="text" class="form-control" v-model="permitForm.holderName"
                       placeholder="Enter full name or organization" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">ID / Passport / Registration *</label>
                <input type="text" class="form-control" v-model="permitForm.idNumber"
                       placeholder="Enter identification number" required>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Phone Number *</label>
                <div class="input-group">
                  <span class="input-group-text">+254</span>
                  <input type="tel" class="form-control" v-model="permitForm.phoneNumber"
                         placeholder="712345678" required pattern="[0-9]{9}">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Email Address *</label>
                <input type="email" class="form-control" v-model="permitForm.email"
                       placeholder="permit.holder@example.com" required>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Physical Address *</label>
                <input type="text" class="form-control" v-model="permitForm.address"
                       placeholder="Enter physical address" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">County *</label>
                <select class="form-select" v-model="permitForm.county" required>
                  <option value="">Select County</option>
                  <option value="Nairobi">Nairobi</option>
                  <option value="Kajiado">Kajiado</option>
                  <option value="Machakos">Machakos</option>
                  <option value="Makueni">Makueni</option>
                  <option value="Other">Other County</option>
                </select>
              </div>
            </div>

            <!-- Permit Beneficiaries (for Family/Corporate) -->
            <div v-if="['FAMILY', 'CORPORATE'].includes(permitForm.permitType)" class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">
                  {{ permitForm.permitType === 'FAMILY' ? 'Family Members' : 'Authorized Personnel' }}
                  <small class="text-muted">(Maximum {{ getMaxBeneficiaries() }} people)</small>
                </h6>
              </div>
              <div class="col-12">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Name</th>
                      <th>ID Number</th>
                      <th>Relationship/Role</th>
                      <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(beneficiary, index) in permitForm.beneficiaries" :key="index">
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="beneficiary.name" placeholder="Full name" required>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="beneficiary.idNumber" placeholder="ID number" required>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="beneficiary.relationship"
                               :placeholder="permitForm.permitType === 'FAMILY' ? 'e.g. Spouse, Child' : 'e.g. Manager, Driver'">
                      </td>
                      <td>
                        <button type="button" @click="removeBeneficiary(index)"
                                class="btn btn-sm btn-danger"
                                :disabled="permitForm.beneficiaries.length === 1">
                          <i class="mdi mdi-delete"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                      <td colspan="4">
                        <button type="button" @click="addBeneficiary()"
                                class="btn btn-sm btn-success"
                                :disabled="permitForm.beneficiaries.length >= getMaxBeneficiaries()">
                          <i class="mdi mdi-plus me-1"></i>Add Person
                        </button>
                      </td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <!-- Vehicle Registration -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">
                  Registered Vehicles
                  <small class="text-muted">(Maximum {{ getMaxVehicles() }} vehicles)</small>
                </h6>
              </div>
              <div class="col-12">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Registration Number</th>
                      <th>Vehicle Type</th>
                      <th>Make & Model</th>
                      <th>Color</th>
                      <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(vehicle, index) in permitForm.vehicles" :key="index">
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="vehicle.registration" placeholder="KAA 123B" required>
                      </td>
                      <td>
                        <select class="form-select form-select-sm" v-model="vehicle.type" required>
                          <option value="">Select Type</option>
                          <option value="Personal Car">Personal Car</option>
                          <option value="SUV">SUV</option>
                          <option value="Safari Van">Safari Van</option>
                          <option value="Tour Bus">Tour Bus</option>
                          <option value="Motorcycle">Motorcycle</option>
                        </select>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="vehicle.makeModel" placeholder="e.g. Toyota Landcruiser">
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="vehicle.color" placeholder="e.g. White">
                      </td>
                      <td>
                        <button type="button" @click="removeVehicle(index)"
                                class="btn btn-sm btn-danger"
                                :disabled="permitForm.vehicles.length === 1">
                          <i class="mdi mdi-delete"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                      <td colspan="5">
                        <button type="button" @click="addVehicle()"
                                class="btn btn-sm btn-success"
                                :disabled="permitForm.vehicles.length >= getMaxVehicles()">
                          <i class="mdi mdi-plus me-1"></i>Add Vehicle
                        </button>
                      </td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <!-- Permit Duration & Validity -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Permit Validity</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label">Start Date *</label>
                <input type="date" class="form-control" v-model="permitForm.startDate"
                       :min="today" @change="updateEndDate()" required>
              </div>
              <div class="col-md-4">
                <label class="form-label">Duration *</label>
                <select class="form-select" v-model="permitForm.duration" @change="updateEndDate()" required>
                  <option value="12">12 Months (1 Year)</option>
                  <option value="6">6 Months</option>
                  <option value="3">3 Months</option>
                  <option value="1">1 Month (Trial)</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">End Date</label>
                <input type="date" class="form-control" v-model="permitForm.endDate" readonly>
              </div>
            </div>

            <!-- Permit Privileges -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Permit Privileges</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.privileges.unlimitedEntry"
                         id="unlimitedEntry" checked disabled>
                  <label class="form-check-label" for="unlimitedEntry">
                    <i class="mdi mdi-infinity text-primary me-2"></i>
                    Unlimited Park Entry
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.privileges.expressLane"
                         id="expressLane">
                  <label class="form-check-label" for="expressLane">
                    <i class="mdi mdi-flash text-warning me-2"></i>
                    Express Lane Access
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.privileges.camping"
                         id="camping">
                  <label class="form-check-label" for="camping">
                    <i class="mdi mdi-tent text-success me-2"></i>
                    Camping Privileges (50% discount)
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.privileges.guidedTours"
                         id="guidedTours">
                  <label class="form-check-label" for="guidedTours">
                    <i class="mdi mdi-hiking text-info me-2"></i>
                    Free Monthly Guided Tours
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.privileges.photography"
                         id="photography">
                  <label class="form-check-label" for="photography">
                    <i class="mdi mdi-camera text-secondary me-2"></i>
                    Photography Permit Included
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.privileges.guestPasses"
                         id="guestPasses">
                  <label class="form-check-label" for="guestPasses">
                    <i class="mdi mdi-ticket text-danger me-2"></i>
                    Monthly Guest Passes ({{ getGuestPasses() }})
                  </label>
                </div>
              </div>
            </div>

            <!-- Payment Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Payment Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Payment Method *</label>
                <select class="form-select" v-model="permitForm.paymentMethod" required>
                  <option value="">Select Payment Method</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="M-Pesa">M-Pesa</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="Monthly Installments">Monthly Installments</option>
                </select>
              </div>
              <div class="col-md-6" v-if="permitForm.paymentMethod === 'Monthly Installments'">
                <label class="form-label">Installment Plan</label>
                <select class="form-select" v-model="permitForm.installmentPlan">
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                </select>
              </div>
            </div>

            <!-- Fee Summary -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-header">
                    <h6 class="mb-0">Permit Fee Summary</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-sm mb-0">
                      <tbody>
                      <tr>
                        <td>{{ getPermitTypeLabel() }} Annual Permit</td>
                        <td class="text-end">{{ formatPrice(permitFees.baseFee) }}</td>
                      </tr>
                      <tr v-if="permitFees.durationDiscount > 0">
                        <td>Duration Adjustment ({{ permitForm.duration }} months)</td>
                        <td class="text-end text-danger">-{{ formatPrice(permitFees.durationDiscount) }}</td>
                      </tr>
                      <tr v-if="permitFees.vehicleFees > 0">
                        <td>Additional Vehicles ({{ permitForm.vehicles.length - 1 }} × {{ formatPrice(5000) }})</td>
                        <td class="text-end">{{ formatPrice(permitFees.vehicleFees) }}</td>
                      </tr>
                      <tr v-if="permitForm.privileges.expressLane">
                        <td>Express Lane Access</td>
                        <td class="text-end">{{ formatPrice(10000) }}</td>
                      </tr>
                      <tr class="table-active">
                        <td><strong>Total Amount</strong></td>
                        <td class="text-end">
                          <h5 class="text-success mb-0">{{ formatPrice(permitFees.totalFee) }}</h5>
                        </td>
                      </tr>
                      <tr v-if="permitForm.paymentMethod === 'Monthly Installments'">
                        <td>Monthly Payment ({{ permitForm.installmentPlan }} months)</td>
                        <td class="text-end">
                          <strong>{{ formatPrice(permitFees.monthlyPayment) }}/month</strong>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="row">
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.agreeToTerms"
                         id="agreeToTerms" required>
                  <label class="form-check-label" for="agreeToTerms">
                    I agree to the <a href="#" class="text-primary">Annual Permit Terms and Conditions</a> *
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="permitForm.autoRenewal"
                         id="autoRenewal">
                  <label class="form-check-label" for="autoRenewal">
                    Enable automatic renewal reminder (30 days before expiry)
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div class="text-muted">
                <small>* Required fields</small>
              </div>
              <div>
                <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-warning" :disabled="loading || !permitForm.agreeToTerms">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="mdi mdi-check me-2"></i>
                  {{ loading ? 'Processing...' : 'Issue Permit ('+formatPrice(permitFees.totalFee)+')' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: "AnnualPermitModal",
  data() {
    return {
      loading: false,
      today: moment().format('YYYY-MM-DD'),

      permitForm: {
        permitType: 'INDIVIDUAL',
        holderName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        address: '',
        county: '',
        beneficiaries: [
          { name: '', idNumber: '', relationship: '' }
        ],
        vehicles: [
          { registration: '', type: '', makeModel: '', color: '' }
        ],
        startDate: '',
        duration: 12,
        endDate: '',
        privileges: {
          unlimitedEntry: true,
          expressLane: false,
          camping: false,
          guidedTours: false,
          photography: false,
          guestPasses: false
        },
        paymentMethod: '',
        installmentPlan: 3,
        agreeToTerms: false,
        autoRenewal: true
      },

      permitTypes: [
        {
          value: 'INDIVIDUAL',
          label: 'Individual',
          description: 'Personal use',
          icon: 'mdi mdi-account',
          price: 50000,
          maxVehicles: 2,
          maxBeneficiaries: 1,
          guestPasses: 2
        },
        {
          value: 'FAMILY',
          label: 'Family',
          description: 'Family package',
          icon: 'mdi mdi-home-account',
          price: 80000,
          maxVehicles: 3,
          maxBeneficiaries: 6,
          guestPasses: 4
        },
        {
          value: 'CORPORATE',
          label: 'Corporate',
          description: 'Business use',
          icon: 'mdi mdi-office-building',
          price: 200000,
          maxVehicles: 10,
          maxBeneficiaries: 20,
          guestPasses: 10
        }
      ],

      permitFees: {
        baseFee: 50000,
        durationDiscount: 0,
        vehicleFees: 0,
        additionalServices: 0,
        totalFee: 50000,
        monthlyPayment: 0
      }
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value || 0);
    },

    selectPermitType(type) {
      this.permitForm.permitType = type;
      this.calculateFees();

      // Reset beneficiaries and vehicles based on type
      if (type === 'INDIVIDUAL') {
        this.permitForm.beneficiaries = [{ name: '', idNumber: '', relationship: '' }];
        this.permitForm.vehicles = [{ registration: '', type: '', makeModel: '', color: '' }];
      }
    },

    getPermitTypeLabel() {
      const type = this.permitTypes.find(t => t.value === this.permitForm.permitType);
      return type ? type.label : 'Individual';
    },

    getMaxBeneficiaries() {
      const type = this.permitTypes.find(t => t.value === this.permitForm.permitType);
      return type ? type.maxBeneficiaries : 1;
    },

    getMaxVehicles() {
      const type = this.permitTypes.find(t => t.value === this.permitForm.permitType);
      return type ? type.maxVehicles : 2;
    },

    getGuestPasses() {
      const type = this.permitTypes.find(t => t.value === this.permitForm.permitType);
      return type ? type.guestPasses : 2;
    },

    addBeneficiary() {
      if (this.permitForm.beneficiaries.length < this.getMaxBeneficiaries()) {
        this.permitForm.beneficiaries.push({ name: '', idNumber: '', relationship: '' });
      }
    },

    removeBeneficiary(index) {
      if (this.permitForm.beneficiaries.length > 1) {
        this.permitForm.beneficiaries.splice(index, 1);
      }
    },

    addVehicle() {
      if (this.permitForm.vehicles.length < this.getMaxVehicles()) {
        this.permitForm.vehicles.push({ registration: '', type: '', makeModel: '', color: '' });
        this.calculateFees();
      }
    },

    removeVehicle(index) {
      if (this.permitForm.vehicles.length > 1) {
        this.permitForm.vehicles.splice(index, 1);
        this.calculateFees();
      }
    },

    updateEndDate() {
      if (this.permitForm.startDate) {
        const endDate = moment(this.permitForm.startDate)
            .add(this.permitForm.duration, 'months')
            .subtract(1, 'day');
        this.permitForm.endDate = endDate.format('YYYY-MM-DD');
        this.calculateFees();
      }
    },

    calculateFees() {
      const permitType = this.permitTypes.find(t => t.value === this.permitForm.permitType);
      if (!permitType) return;

      // Base fee
      this.permitFees.baseFee = permitType.price;

      // Duration discount/adjustment
      if (this.permitForm.duration < 12) {
        const monthlyRate = permitType.price / 12;
        const adjustedFee = monthlyRate * this.permitForm.duration;
        this.permitFees.durationDiscount = permitType.price - adjustedFee;
      } else {
        this.permitFees.durationDiscount = 0;
      }

      // Additional vehicle fees (first vehicle is included)
      const additionalVehicles = Math.max(0, this.permitForm.vehicles.length - 1);
      this.permitFees.vehicleFees = additionalVehicles * 5000;

      // Additional services
      let services = 0;
      if (this.permitForm.privileges.expressLane) services += 10000;
      this.permitFees.additionalServices = services;

      // Total
      this.permitFees.totalFee = this.permitFees.baseFee -
          this.permitFees.durationDiscount +
          this.permitFees.vehicleFees +
          this.permitFees.additionalServices;

      // Monthly payment calculation
      if (this.permitForm.paymentMethod === 'Monthly Installments') {
        this.permitFees.monthlyPayment = Math.ceil(this.permitFees.totalFee / this.permitForm.installmentPlan);
      }
    },

    generatePermitNumber() {
      const date = new Date();
      const year = date.getFullYear();
      const typePrefix = {
        'INDIVIDUAL': 'IND',
        'FAMILY': 'FAM',
        'CORPORATE': 'COR'
      };
      const prefix = typePrefix[this.permitForm.permitType] || 'IND';
      const sequence = String(Math.floor(Math.random() * 10000)).padStart(5, '0');
      return `AMB-${prefix}-${year}-${sequence}`;
    },

    async issuePermit() {
      try {
        // Validate vehicles
        const validVehicles = this.permitForm.vehicles.filter(v => v.registration && v.type);
        if (validVehicles.length === 0) {
          alert('Please add at least one vehicle with registration and type');
          return;
        }

        // Validate beneficiaries for family/corporate permits
        if (['FAMILY', 'CORPORATE'].includes(this.permitForm.permitType)) {
          const validBeneficiaries = this.permitForm.beneficiaries.filter(b => b.name && b.idNumber);
          if (validBeneficiaries.length === 0) {
            alert('Please add at least one beneficiary');
            return;
          }
        }

        this.loading = true;

        // Create permit data
        const permitData = {
          permitNumber: this.generatePermitNumber(),
          permitType: this.permitForm.permitType,
          holderName: this.permitForm.holderName,
          idNumber: this.permitForm.idNumber,
          phoneNumber: '+254' + this.permitForm.phoneNumber,
          email: this.permitForm.email,
          address: this.permitForm.address,
          county: this.permitForm.county,
          beneficiaries: this.permitForm.beneficiaries.filter(b => b.name),
          vehicles: validVehicles,
          startDate: this.permitForm.startDate,
          endDate: this.permitForm.endDate,
          duration: this.permitForm.duration,
          privileges: { ...this.permitForm.privileges },
          paymentMethod: this.permitForm.paymentMethod,
          installmentPlan: this.permitForm.paymentMethod === 'Monthly Installments' ? this.permitForm.installmentPlan : null,
          fees: { ...this.permitFees },
          autoRenewal: this.permitForm.autoRenewal,
          status: 'ACTIVE',
          issuedDate: new Date().toISOString(),
          issuedBy: sessionStorage.getItem("sessionNames") || 'System Admin'
        };

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        this.$emit('permit-issued', permitData);

        // Show success message
        if (confirm('Annual permit issued successfully! Print permit card now?')) {
          this.printPermitCard(permitData);
        }

        // Reset form
        this.resetForm();

        // Close modal
        const modal = bootstrap.Modal.getInstance(document.getElementById('annualPermitModal'));
        modal.hide();

      } catch (error) {
        console.error('Error issuing permit:', error);
        alert('Failed to issue permit. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    printPermitCard(permitData) {
      // Create a new window for the permit card
      const printWindow = window.open('', '_blank', 'width=900,height=700');

      // Generate the permit card HTML with professional design
      const permitCardHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Annual Permit - ${permitData.permitNumber}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
          <link href="https://cdn.jsdelivr.net/npm/@mdi/font@6.5.95/css/materialdesignicons.min.css" rel="stylesheet">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: #f5f5f5;
              color: #2c3e50;
              line-height: 1.6;
            }

            .permit-container {
              max-width: 850px;
              margin: 0 auto;
            }

            /* Front Card */
            .permit-card {
              background: white;
              border-radius: 16px;
              overflow: hidden;
              box-shadow: 0 20px 40px rgba(0,0,0,0.1);
              margin-bottom: 30px;
              position: relative;
            }

            /* Header Section */
            .permit-header {
              background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
              color: white;
              padding: 30px;
              position: relative;
              overflow: hidden;
            }

            .permit-header::before {
              content: '';
              position: absolute;
              width: 500px;
              height: 500px;
              background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
              top: -250px;
              right: -250px;
            }

            .header-content {
              position: relative;
              z-index: 1;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }

            .official-info h6 {
              font-size: 12px;
              font-weight: 600;
              opacity: 0.9;
              margin: 0;
              letter-spacing: 1px;
              text-transform: uppercase;
            }

            .official-info h4 {
              font-size: 28px;
              font-weight: 700;
              margin: 8px 0;
              text-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }

            .official-info p {
              font-size: 14px;
              opacity: 0.85;
              margin: 0;
              letter-spacing: 0.5px;
            }

            .logos {
              display: flex;
              gap: 20px;
              align-items: center;
            }

            .logo {
              width: 70px;
              height: 70px;
              background: rgba(255,255,255,0.15);
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              backdrop-filter: blur(10px);
            }

            .logo img {
              width: 50px;
              height: 50px;
              filter: brightness(0) invert(1);
              opacity: 0.9;
            }

            /* Permit Type Banner */
            .permit-type-banner {
              background: ${permitData.permitType === 'CORPORATE' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
          permitData.permitType === 'FAMILY' ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' :
              'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'};
              padding: 20px 30px;
              text-align: center;
              color: white;
              position: relative;
              overflow: hidden;
            }

            .permit-type-banner::before {
              content: '';
              position: absolute;
              width: 100%;
              height: 100%;
              background: repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255,255,255,0.05) 10px,
                rgba(255,255,255,0.05) 20px
              );
            }

            .permit-type-title {
              font-size: 24px;
              font-weight: 700;
              margin: 0;
              letter-spacing: 3px;
              text-transform: uppercase;
              position: relative;
              z-index: 1;
            }

            .permit-number {
              font-size: 18px;
              font-weight: 600;
              margin-top: 8px;
              opacity: 0.95;
              letter-spacing: 2px;
              position: relative;
              z-index: 1;
            }

            /* Main Content */
            .permit-body {
              padding: 30px;
            }

            /* Holder Section */
            .holder-section {
              display: flex;
              gap: 30px;
              margin-bottom: 30px;
              padding: 25px;
              background: #f8f9fa;
              border-radius: 12px;
              border: 1px solid #e9ecef;
            }

            .photo-container {
              flex-shrink: 0;
            }

            .photo-box {
              width: 140px;
              height: 180px;
              background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
              border-radius: 12px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              border: 3px solid #2196f3;
              position: relative;
              overflow: hidden;
            }

            .photo-box::before {
              content: '';
              position: absolute;
              top: -50%;
              left: -50%;
              width: 200%;
              height: 200%;
              background: repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255,255,255,0.1) 10px,
                rgba(255,255,255,0.1) 20px
              );
            }

            .photo-box i {
              font-size: 64px;
              color: #1976d2;
              margin-bottom: 10px;
            }

            .photo-box span {
              font-size: 12px;
              font-weight: 600;
              color: #1976d2;
              text-transform: uppercase;
              letter-spacing: 1px;
            }

            .holder-details {
              flex-grow: 1;
            }

            .detail-row {
              display: flex;
              margin-bottom: 12px;
              align-items: center;
            }

            .detail-label {
              font-size: 12px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              min-width: 140px;
              font-weight: 500;
            }

            .detail-value {
              font-size: 16px;
              font-weight: 600;
              color: #2c3e50;
              flex-grow: 1;
            }

            .detail-value.large {
              font-size: 20px;
              color: #1976d2;
            }

            .validity-dates {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-top: 20px;
              padding-top: 20px;
              border-top: 2px solid #e9ecef;
            }

            .date-box {
              padding: 15px;
              background: white;
              border-radius: 8px;
              border: 1px solid #e9ecef;
              text-align: center;
            }

            .date-box.valid-from {
              border-color: #4caf50;
              background: #f1f8e9;
            }

            .date-box.valid-until {
              border-color: #f44336;
              background: #ffebee;
            }

            .date-label {
              font-size: 11px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 5px;
            }

            .date-value {
              font-size: 18px;
              font-weight: 700;
            }

            .valid-from .date-value {
              color: #4caf50;
            }

            .valid-until .date-value {
              color: #f44336;
            }

            /* QR Code Section */
            .qr-section {
              position: absolute;
              top: 30px;
              right: 30px;
              text-align: center;
              background: white;
              padding: 15px;
              border-radius: 12px;
              box-shadow: 0 8px 16px rgba(0,0,0,0.1);
            }

            .qr-code {
              width: 120px;
              height: 120px;
              background: #f8f9fa;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 8px;
            }

            .qr-code i {
              font-size: 80px;
              color: #333;
            }

            .qr-label {
              font-size: 10px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            /* Privileges Section */
            .privileges-section {
              margin-bottom: 30px;
            }

            .section-header {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }

            .section-icon {
              width: 40px;
              height: 40px;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              border-radius: 10px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
            }

            .section-title {
              font-size: 18px;
              font-weight: 700;
              color: #2c3e50;
              margin: 0;
            }

            .privileges-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
            }

            .privilege-item {
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
              padding: 15px;
              border-radius: 10px;
              display: flex;
              align-items: center;
              gap: 12px;
              transition: transform 0.2s;
            }

            .privilege-item:hover {
              transform: translateY(-2px);
            }

            .privilege-icon {
              width: 36px;
              height: 36px;
              background: white;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #667eea;
              font-size: 20px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }

            .privilege-text {
              font-size: 13px;
              font-weight: 600;
              color: #2c3e50;
              line-height: 1.3;
            }

            /* Vehicles Section */
            .vehicles-section {
              margin-bottom: 30px;
            }

            .vehicle-list {
              display: grid;
              gap: 15px;
            }

            .vehicle-item {
              background: #e3f2fd;
              padding: 20px;
              border-radius: 10px;
              display: flex;
              align-items: center;
              gap: 20px;
              border: 1px solid #bbdefb;
            }

            .vehicle-icon-box {
              width: 50px;
              height: 50px;
              background: white;
              border-radius: 10px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #1976d2;
              font-size: 28px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }

            .vehicle-info {
              flex-grow: 1;
            }

            .vehicle-reg {
              font-size: 20px;
              font-weight: 700;
              color: #1976d2;
              margin-bottom: 5px;
            }

            .vehicle-details {
              font-size: 14px;
              color: #555;
            }

            /* Footer */
            .permit-footer {
              background: #2c3e50;
              color: white;
              padding: 25px 30px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }

            .signature-section {
              display: flex;
              align-items: flex-end;
              gap: 30px;
            }

            .signature-box {
              text-align: center;
            }

            .signature-line {
              width: 200px;
              height: 50px;
              border-bottom: 2px solid white;
              margin-bottom: 8px;
            }

            .signature-label {
              font-size: 12px;
              opacity: 0.8;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .issue-info {
              text-align: right;
            }

            .issue-info p {
              margin: 0;
              font-size: 12px;
              opacity: 0.9;
              line-height: 1.8;
            }

            /* Back Card */
            .back-card {
              page-break-before: always;
            }

            .back-header {
              background: #34495e;
              color: white;
              padding: 20px 30px;
              text-align: center;
            }

            .back-header h5 {
              font-size: 20px;
              font-weight: 700;
              margin: 0;
              letter-spacing: 2px;
              text-transform: uppercase;
            }

            .beneficiaries-section, .terms-section, .contact-section {
              padding: 30px;
            }

            .table-modern {
              width: 100%;
              border-collapse: collapse;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 4px 8px rgba(0,0,0,0.05);
            }

            .table-modern th {
              background: #f5f7fa;
              padding: 15px;
              text-align: left;
              font-weight: 600;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #666;
              border-bottom: 2px solid #e9ecef;
            }

            .table-modern td {
              padding: 15px;
              border-bottom: 1px solid #f0f0f0;
              color: #2c3e50;
            }

            .table-modern tr:last-child td {
              border-bottom: none;
            }

            .terms-box {
              background: #fff9c4;
              border: 2px solid #f9a825;
              border-radius: 12px;
              padding: 25px;
            }

            .terms-title {
              display: flex;
              align-items: center;
              gap: 10px;
              font-size: 16px;
              font-weight: 700;
              color: #f57c00;
              margin-bottom: 15px;
            }

            .terms-list {
              list-style: none;
              padding: 0;
            }

            .terms-list li {
              padding: 8px 0;
              padding-left: 25px;
              position: relative;
              color: #795548;
              font-size: 13px;
              line-height: 1.6;
            }

            .terms-list li:before {
              content: '✓';
              position: absolute;
              left: 0;
              color: #f9a825;
              font-weight: bold;
            }

            .contact-box {
              background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
              color: white;
              border-radius: 12px;
              padding: 25px;
              text-align: center;
            }

            .contact-title {
              font-size: 16px;
              font-weight: 700;
              margin-bottom: 20px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }

            .contact-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 20px;
              margin-top: 20px;
            }

            .contact-item {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            }

            .contact-item i {
              font-size: 20px;
              color: #f39c12;
            }

            .contact-item span {
              font-size: 14px;
            }

            .website-info {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 1px solid rgba(255,255,255,0.2);
              font-size: 13px;
              opacity: 0.9;
            }

            @media print {
              body {
                margin: 0;
                background: white;
                padding: 0;
              }
              .permit-card {
                box-shadow: none;
                margin-bottom: 0;
                border: 2px solid #000;
              }
              .back-card {
                margin-top: 0;
              }
            }
          </style>
        </head>
        <body onload="window.print()">
          <div class="permit-container">
            <!-- Front of Card -->
            <div class="permit-card">
              <!-- Header -->
              <div class="permit-header">
                <div class="header-content">
                  <div class="official-info">
                    <h6>Republic of Kenya</h6>
                    <h4>AMBOSELI NATIONAL PARK</h4>
                    <p>Kenya Wildlife Service</p>
                  </div>
                  <div class="logos">
                    <div class="logo">
                      <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='white' d='M50 10 L30 30 L30 60 L50 80 L70 60 L70 30 Z'/%3E%3C/svg%3E" alt="Kenya">
                    </div>
                    <div class="logo">
                      <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='40' fill='white'/%3E%3C/svg%3E" alt="KWS">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Permit Type Banner -->
              <div class="permit-type-banner">
                <h2 class="permit-type-title">
                  ${permitData.permitType === 'CORPORATE' ? '🏢' : permitData.permitType === 'FAMILY' ? '👨‍👩‍👧‍👦' : '👤'}
                  ${permitData.permitType} ANNUAL PERMIT
                </h2>
                <div class="permit-number">${permitData.permitNumber}</div>
              </div>

              <!-- QR Code -->
              <div class="qr-section">
                <div class="qr-code">
                  <i class="mdi mdi-qrcode"></i>
                </div>
                <div class="qr-label">Scan to Verify</div>
              </div>

              <!-- Permit Body -->
              <div class="permit-body">
                <!-- Holder Information -->
                <div class="holder-section">
                  <div class="photo-container">
                    <div class="photo-box">
                      <i class="mdi mdi-account"></i>
                      <span>Photo</span>
                    </div>
                  </div>

                  <div class="holder-details">
                    <div class="detail-row">
                      <span class="detail-label">Permit Holder</span>
                      <span class="detail-value large">${permitData.holderName}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">ID/Passport No</span>
                      <span class="detail-value">${permitData.idNumber}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Contact Number</span>
                      <span class="detail-value">${permitData.phoneNumber}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Email Address</span>
                      <span class="detail-value">${permitData.email}</span>
                    </div>

                    <div class="validity-dates">
                      <div class="date-box valid-from">
                        <div class="date-label">Valid From</div>
                        <div class="date-value">${moment(permitData.startDate).format('DD MMM YYYY')}</div>
                      </div>
                      <div class="date-box valid-until">
                        <div class="date-label">Valid Until</div>
                        <div class="date-value">${moment(permitData.endDate).format('DD MMM YYYY')}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Privileges -->
                <div class="privileges-section">
                  <div class="section-header">
                    <div class="section-icon">
                      <i class="mdi mdi-star"></i>
                    </div>
                    <h3 class="section-title">Permit Privileges</h3>
                  </div>

                  <div class="privileges-grid">
                    ${permitData.privileges.unlimitedEntry ? `
                      <div class="privilege-item">
                        <div class="privilege-icon">
                          <i class="mdi mdi-infinity"></i>
                        </div>
                        <div class="privilege-text">Unlimited Park Entry</div>
                      </div>
                    ` : ''}
                    ${permitData.privileges.expressLane ? `
                      <div class="privilege-item">
                        <div class="privilege-icon">
                          <i class="mdi mdi-flash"></i>
                        </div>
                        <div class="privilege-text">Express Lane Access</div>
                      </div>
                    ` : ''}
                    ${permitData.privileges.camping ? `
                      <div class="privilege-item">
                        <div class="privilege-icon">
                          <i class="mdi mdi-tent"></i>
                        </div>
                        <div class="privilege-text">Camping 50% Discount</div>
                      </div>
                    ` : ''}
                    ${permitData.privileges.guidedTours ? `
                      <div class="privilege-item">
                        <div class="privilege-icon">
                          <i class="mdi mdi-hiking"></i>
                        </div>
                        <div class="privilege-text">Free Monthly Tours</div>
                      </div>
                    ` : ''}
                    ${permitData.privileges.photography ? `
                      <div class="privilege-item">
                        <div class="privilege-icon">
                          <i class="mdi mdi-camera"></i>
                        </div>
                        <div class="privilege-text">Photography Permit</div>
                      </div>
                    ` : ''}
                    ${permitData.privileges.guestPasses ? `
                      <div class="privilege-item">
                        <div class="privilege-icon">
                          <i class="mdi mdi-ticket"></i>
                        </div>
                        <div class="privilege-text">${permitData.permitType === 'CORPORATE' ? '10' : permitData.permitType === 'FAMILY' ? '4' : '2'} Guest Passes/Month</div>
                      </div>
                    ` : ''}
                  </div>
                </div>

                <!-- Registered Vehicles -->
                <div class="vehicles-section">
                  <div class="section-header">
                    <div class="section-icon" style="background: linear-gradient(135deg, #56ccf2 0%, #2f80ed 100%);">
                      <i class="mdi mdi-car"></i>
                    </div>
                    <h3 class="section-title">Registered Vehicles</h3>
                  </div>

                  <div class="vehicle-list">
                    ${permitData.vehicles.map(vehicle => `
                      <div class="vehicle-item">
                        <div class="vehicle-icon-box">
                          <i class="mdi mdi-car-side"></i>
                        </div>
                        <div class="vehicle-info">
                          <div class="vehicle-reg">${vehicle.registration}</div>
                          <div class="vehicle-details">
                            ${vehicle.type} ${vehicle.makeModel ? `• ${vehicle.makeModel}` : ''} ${vehicle.color ? `• ${vehicle.color}` : ''}
                          </div>
                        </div>
                      </div>
                    `).join('')}
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="permit-footer">
                <div class="signature-section">
                  <div class="signature-box">
                    <div class="signature-line"></div>
                    <div class="signature-label">Authorized Signature</div>
                  </div>
                  <div class="signature-box">
                    <div class="signature-line"></div>
                    <div class="signature-label">Permit Holder</div>
                  </div>
                </div>
                <div class="issue-info">
                  <p><strong>Issue Date:</strong> ${moment(permitData.issuedDate).format('DD MMM YYYY')}</p>
                  <p><strong>Issued By:</strong> ${permitData.issuedBy}</p>
                  <p><strong>Payment:</strong> KES ${permitData.fees.totalFee.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <!-- Back of Card -->
            <div class="permit-card back-card">
              <div class="back-header">
                <h5>Terms, Conditions & Additional Information</h5>
              </div>

              ${permitData.beneficiaries && permitData.beneficiaries.length > 0 && permitData.beneficiaries[0].name ? `
                <div class="beneficiaries-section">
                  <div class="section-header">
                    <div class="section-icon" style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
                      <i class="mdi mdi-account-multiple"></i>
                    </div>
                    <h3 class="section-title">Authorized Beneficiaries</h3>
                  </div>

                  <table class="table-modern">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>ID Number</th>
                        <th>Relationship/Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${permitData.beneficiaries.filter(b => b.name).map(beneficiary => `
                        <tr>
                          <td><strong>${beneficiary.name}</strong></td>
                          <td>${beneficiary.idNumber}</td>
                          <td>${beneficiary.relationship || '-'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
              ` : ''}

              <div class="terms-section">
                <div class="terms-box">
                  <div class="terms-title">
                    <i class="mdi mdi-shield-check"></i>
                    Terms & Conditions
                  </div>
                  <ul class="terms-list">
                    <li>This permit is non-transferable and must be presented with valid ID upon entry</li>
                    <li>All park rules and regulations must be strictly observed at all times</li>
                    <li>Speed limit of 40km/h must be maintained within the park premises</li>
                    <li>Feeding, disturbing, or harassing wildlife is strictly prohibited</li>
                    <li>All visitors must exit the park by 6:30 PM unless authorized for camping</li>
                    <li>The permit may be revoked for violation of park rules without refund</li>
                    <li>Lost permits must be reported immediately and may incur replacement fees</li>
                    <li>Commercial activities require additional permits and authorization</li>
                  </ul>
                </div>
              </div>

              <div class="contact-section">
                <div class="contact-box">
                  <div class="contact-title">Emergency Contacts & Information</div>

                  <div class="contact-grid">
                    <div class="contact-item">
                      <i class="mdi mdi-phone-alert"></i>
                      <span><strong>Emergency:</strong> 999</span>
                    </div>
                    <div class="contact-item">
                      <i class="mdi mdi-phone"></i>
                      <span><strong>Park Office:</strong> +254 20 2379407</span>
                    </div>
                    <div class="contact-item">
                      <i class="mdi mdi-email"></i>
                      <span>amboseli@kws.go.ke</span>
                    </div>
                    <div class="contact-item">
                      <i class="mdi mdi-radio"></i>
                      <span><strong>Radio:</strong> Channel 16</span>
                    </div>
                  </div>

                  <div class="website-info">
                    <i class="mdi mdi-web"></i> www.kws.go.ke |
                    <i class="mdi mdi-facebook"></i> @kenyawildlifeservice
                  </div>
                </div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(permitCardHTML);
      printWindow.document.close();
    },

    resetForm() {
      this.permitForm = {
        permitType: 'INDIVIDUAL',
        holderName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        address: '',
        county: '',
        beneficiaries: [
          { name: '', idNumber: '', relationship: '' }
        ],
        vehicles: [
          { registration: '', type: '', makeModel: '', color: '' }
        ],
        startDate: '',
        duration: 12,
        endDate: '',
        privileges: {
          unlimitedEntry: true,
          expressLane: false,
          camping: false,
          guidedTours: false,
          photography: false,
          guestPasses: false
        },
        paymentMethod: '',
        installmentPlan: 3,
        agreeToTerms: false,
        autoRenewal: true
      };
      this.calculateFees();
    }
  },
  mounted() {
    this.calculateFees();
  }
}
</script>

<style scoped>
.modal-dialog {
  max-width: 900px;
}

.permit-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.permit-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

.card {
  border-radius: 8px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.table-active {
  background-color: #f0f0f0;
}

.font-size-24 {
  font-size: 24px;
}

.form-control-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.table td {
  vertical-align: middle;
}
</style>